import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { z } from 'zod';
import { ISupplier_Type } from '@bridebook/models/source/models/Suppliers.types';
import { IAddress, IDatePicker } from '@bridebook/models/source/models/Weddings.types';
import { getI18n } from '@bridebook/web/lib/i18n/getI18n';
import { PARTNER_NAME_MAX_LENGTH } from '@bridebook/toolbox/src/validation/partner-names/const';
import { createPartnerNameSchema } from '@bridebook/web/lib/validation/partner-names/schema';
import { weddingDateSchema } from '@bridebook/web/lib/datepicker/schema';
import { getLIVE_27666IntentOptions } from '@bridebook/web/lib/enquiries/intent';
import type { ILeadIntentOption } from '@bridebook/models/source/models/Suppliers/Leads.types';
import { isSupplierVenueType } from '@bridebook/web/lib/supplier/utils';

export const UNDECIDED_DATE: IDatePicker = { y: null, m: null, d: null };

const locationSchema = z.custom<IAddress>((val) => typeof val === 'object' && val !== null);

const intentSchema = z.object({
  brochure: z.boolean(),
  pricing: z.boolean(),
  availability: z.boolean(),
  showround: z.boolean(),
  moreAvailability: z.boolean(),
  other: z.boolean(),
});

const createAtLeastOneIntentSchema = (
  options: readonly ILeadIntentOption[],
  message = getI18n().t('enquiries:error.chooseAtLeastOneOption'),
) =>
  intentSchema.refine(
    (intent) => options.some((key) => intent[key]),
    () => ({
      message,
    }),
  );

const phoneSchema = z.string().refine(
  (value) => {
    try {
      return parsePhoneNumberWithError(value).isPossible();
    } catch {
      return false;
    }
  },
  () => ({ message: getI18n().t('common:validation.invalid.phone') }),
);

const createEnquiryFormBaseSchema = () => {
  const partnerNameSchema = createPartnerNameSchema({ allowEmpty: false });
  return z.object({
    email: z.string().email(getI18n().t('error.fillOutMissingFields')),
    phone: phoneSchema,
    partners: z.tuple([partnerNameSchema, partnerNameSchema]),
    weddingDate: weddingDateSchema.nullable(),
    guestsInitialTarget: z.number().min(1, getI18n().t('error.fillOutMissingFields')),
    location: locationSchema.nullable(),
    message: z.string(),
    intent: intentSchema,
  });
};

// Keep a static instance for type inference only - messages from this are never shown to users.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const enquiryFormBaseSchema = createEnquiryFormBaseSchema();
export type EnquiryFormValues = z.infer<typeof enquiryFormBaseSchema>;

const requiredLocationSchema = locationSchema.nullable().refine(
  (val): val is IAddress => val !== null,
  () => ({ message: getI18n().t('error.fillOutMissingFields') }),
);

const requiredWeddingDateSchema = weddingDateSchema.nullable().refine(
  (val): val is z.infer<typeof weddingDateSchema> => val !== null,
  () => ({ message: getI18n().t('error.fillOutMissingFields') }),
);

// Accepts ISupplier.type (ISupplier_Type[]) or IUISupplier.type (ISupplier_Type)
const isVenueType = (supplierType: ISupplier_Type | ISupplier_Type[]) =>
  isSupplierVenueType(Array.isArray(supplierType) ? supplierType[0] : supplierType);

const requiredIntentSchema = intentSchema.refine(
  (val) => Object.values(val).some(Boolean),
  () => ({ message: getI18n().t('enquiries:error.chooseAtLeastOneOption') }),
);

// Submitting an enquiry — message required, weddingDate must be set, location required for non-venues
export const createEnquiryFormSendSchema = ({
  supplierType,
  requireIntent = false,
}: {
  supplierType: ISupplier_Type | ISupplier_Type[];
  requireIntent?: boolean;
}) =>
  createEnquiryFormBaseSchema().extend({
    message: z.string().min(1, { message: getI18n().t('enquiries:error.enterMessage') }),
    weddingDate: requiredWeddingDateSchema,
    location: isVenueType(supplierType) ? locationSchema.nullable() : requiredLocationSchema,
    intent: requireIntent ? requiredIntentSchema : intentSchema,
  });

/** LIVE-27666 enquiry form variant: guests and wedding date optional on send; message and at least one displayed intent required. */
export const LIVE_27666_createEnquiryFormSendSchema = (isVenue: boolean) =>
  enquiryFormBaseSchema.extend({
    partners: z.tuple([
      createPartnerNameSchema({
        allowEmpty: false,
        messages: {
          required: getI18n().t('enquiries:v2.form.partners.0.error.required'),
          tooLong: getI18n().t('enquiries:v2.form.partners.0.error.tooLong', {
            maxLength: PARTNER_NAME_MAX_LENGTH,
          }),
          containsNumbers: getI18n().t('enquiries:v2.form.partners.0.error.containsNumbers'),
          containsInvalidCharacters: getI18n().t(
            'enquiries:v2.form.partners.0.error.containsInvalidCharacters',
          ),
          invalid: getI18n().t('enquiries:v2.form.partners.0.error.invalid'),
        },
      }),
      createPartnerNameSchema({
        allowEmpty: false,
        messages: {
          required: getI18n().t('enquiries:v2.form.partners.1.error.required'),
          tooLong: getI18n().t('enquiries:v2.form.partners.1.error.tooLong', {
            maxLength: PARTNER_NAME_MAX_LENGTH,
          }),
          containsNumbers: getI18n().t('enquiries:v2.form.partners.1.error.containsNumbers'),
          containsInvalidCharacters: getI18n().t(
            'enquiries:v2.form.partners.1.error.containsInvalidCharacters',
          ),
          invalid: getI18n().t('enquiries:v2.form.partners.1.error.invalid'),
        },
      }),
    ]),
    email: z.string().email(getI18n().t('enquiries:v2.form.email.error.invalid')),
    phone: z.string().refine(
      (value) => {
        try {
          return parsePhoneNumberWithError(value).isPossible();
        } catch {
          return false;
        }
      },
      () => ({
        message: getI18n().t('enquiries:v2.form.phone.error.invalid'),
      }),
    ),
    message: z.string().min(1, {
      message: getI18n().t('enquiries:v2.form.message.error.required'),
    }),
    weddingDate: weddingDateSchema.nullable(),
    location: isVenue
      ? locationSchema.nullable()
      : locationSchema.nullable().refine(
          (val): val is IAddress => val !== null,
          () => ({
            message: getI18n().t('enquiries:v2.form.location.error.required'),
          }),
        ),
    intent: createAtLeastOneIntentSchema(
      getLIVE_27666IntentOptions(isVenue),
      getI18n().t('enquiries:v2.form.intent.error.required'),
    ),
    guestsInitialTarget: z.number().min(0),
  });

// Saving edited profile fields from the edit screen — no message, weddingDate must be set, location required for non-venues
export const createEnquiryFormEditSchema = (supplierType: ISupplier_Type | ISupplier_Type[]) =>
  createEnquiryFormBaseSchema().extend({
    message: z.string().optional(),
    weddingDate: requiredWeddingDateSchema,
    location: isVenueType(supplierType) ? locationSchema.nullable() : requiredLocationSchema,
  });
